import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import { ArrowLeftRight, Building2, FileText, Plus, Truck, Upload } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { api, type Page } from "@/lib/api.ts";
import { useSession } from "@/lib/session.tsx";
import { useDebounce } from "@/hooks/use-debounce.ts";
import { useHotkey } from "@/hooks/use-hotkey.ts";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut } from "@/components/ui/command.tsx";
import { Spinner } from "@/components/ui/spinner.tsx";
import { StatusPill } from "./status-pill.tsx";
import { navFor } from "./nav.ts";

interface PaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

export const useCommandPalette = create<PaletteState>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
  toggle: () => set((s) => ({ open: !s.open })),
}));

interface QuickAction {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
  search?: Record<string, unknown>;
  shortcut?: string;
}

interface CompanyHit {
  id: number;
  name: string;
  label?: string | null;
}
interface DocumentHit {
  id: number;
  publicId?: string;
  filename: string | null;
  reference?: string | null;
  status: string;
  documentTypeCode?: string | null;
  tradingPartner?: string | null;
}
interface PartnerHit {
  id: number;
  name: string;
  code?: string | null;
}

function items<T>(data: Page<T> | T[] | undefined): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : (data.items ?? []);
}

export function CommandPalette() {
  const { open, setOpen } = useCommandPalette();
  const session = useSession();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const q = useDebounce(query.trim(), 250);
  const enabled = open && q.length >= 2;

  useHotkey("mod+k", () => setOpen(!open), { allowInInputs: true });

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const groups = useMemo(() => navFor(session), [session]);
  const actions = useMemo<QuickAction[]>(() => {
    const portal = session.surface === "portal";
    const list: QuickAction[] = [];
    if (portal ? session.me?.canUpload : true) list.push({ id: "upload", label: "Upload document", icon: Upload, to: portal ? "/upload" : "/documents", search: portal ? undefined : { view: "upload" } });
    if (!portal || session.hasFeature("customer_partner_setup")) list.push({ id: "partner", label: "New trading partner", icon: ArrowLeftRight, to: "/partners/new" });
    list.push({ id: "shipment", label: "New shipment", icon: Truck, to: "/shipments", search: { view: "new" } });
    return list;
  }, [session]);

  const companies = useQuery({
    queryKey: ["palette", "companies", q],
    queryFn: ({ signal }) => api.get<Page<CompanyHit> | CompanyHit[]>("/api/companies", { query: { q, pageSize: 5 }, signal }),
    enabled: enabled && session.isOperator,
    staleTime: 30_000,
  });
  const documents = useQuery({
    queryKey: ["palette", "documents", q],
    queryFn: ({ signal }) => api.get<Page<DocumentHit> | DocumentHit[]>("/api/documents", { query: { q, pageSize: 5 }, signal }),
    enabled,
    staleTime: 30_000,
  });
  const partners = useQuery({
    queryKey: ["palette", "partners", q],
    queryFn: ({ signal }) => api.get<Page<PartnerHit> | PartnerHit[]>("/api/partners", { query: { q, pageSize: 5 }, signal }),
    enabled,
    staleTime: 30_000,
  });

  const searching = enabled && (companies.isFetching || documents.isFetching || partners.isFetching);
  const lower = query.trim().toLowerCase();
  const navHits = useMemo(() => groups.map((g) => ({ ...g, items: g.items.filter((i) => !lower || i.label.toLowerCase().includes(lower) || g.label.toLowerCase().includes(lower)) })).filter((g) => g.items.length > 0), [groups, lower]);
  const actionHits = actions.filter((a) => !lower || a.label.toLowerCase().includes(lower));

  const go = (to: string, search?: Record<string, unknown>) => {
    setOpen(false);
    void navigate({ to, search } as never);
  };

  const companyHits = items(companies.data);
  const documentHits = items(documents.data);
  const partnerHits = items(partners.data);
  const nothing = navHits.length === 0 && actionHits.length === 0 && companyHits.length === 0 && documentHits.length === 0 && partnerHits.length === 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen} title="Search and commands" description="Jump to a page, run an action or find a record" shouldFilter={false}>
      <CommandInput placeholder="Search pages, companies, documents, partners…" value={query} onValueChange={setQuery} />
      <CommandList>
        {searching && (
          <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
            <Spinner size={12} /> Searching…
          </div>
        )}
        {nothing && !searching && <CommandEmpty>No results for “{query}”.</CommandEmpty>}
        {actionHits.length > 0 && (
          <CommandGroup heading="Quick actions">
            {actionHits.map((a) => (
              <CommandItem key={a.id} value={`action-${a.id}`} onSelect={() => go(a.to, a.search)}>
                <Plus className="text-primary" />
                <span>{a.label}</span>
                {a.shortcut && <CommandShortcut>{a.shortcut}</CommandShortcut>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {companyHits.length > 0 && (
          <CommandGroup heading="Companies">
            {companyHits.map((c) => (
              <CommandItem key={c.id} value={`company-${c.id}`} onSelect={() => go(`/companies/${c.id}`)}>
                <Building2 />
                <span className="truncate">{c.label ?? c.name}</span>
                {c.label && c.label !== c.name && <span className="mono ml-auto text-xs text-muted-foreground">{c.name}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {documentHits.length > 0 && (
          <CommandGroup heading="Documents">
            {documentHits.map((d) => (
              <CommandItem key={d.id} value={`document-${d.id}`} onSelect={() => go(`/documents/${d.id}`)}>
                <FileText />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate">{d.reference ?? d.filename ?? d.publicId ?? `#${d.id}`}</span>
                  <span className="truncate text-xs text-muted-foreground">
                    {[d.documentTypeCode, d.tradingPartner, d.filename].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <StatusPill status={d.status} size="sm" />
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {partnerHits.length > 0 && (
          <CommandGroup heading="Trading partners">
            {partnerHits.map((p) => (
              <CommandItem key={p.id} value={`partner-${p.id}`} onSelect={() => go(`/partners/${p.id}`)}>
                <ArrowLeftRight />
                <span className="truncate">{p.name}</span>
                {p.code && <span className="mono ml-auto text-xs text-muted-foreground">{p.code}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {(actionHits.length > 0 || companyHits.length > 0 || documentHits.length > 0 || partnerHits.length > 0) && navHits.length > 0 && <CommandSeparator />}
        {navHits.map((g) => (
          <CommandGroup key={g.label} heading={g.label}>
            {g.items.map((i) => (
              <CommandItem key={i.to} value={`nav-${i.to}`} onSelect={() => go(i.to)}>
                <i.icon />
                <span>{i.label}</span>
                <CommandShortcut className="mono tracking-normal">{i.to}</CommandShortcut>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

export default CommandPalette;
